import { useState, useRef, useEffect, useCallback } from "react";
import { MessageCircle, X, Send, Loader2, RotateCcw, Sparkles, Check, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ChatMessage from "@/components/ChatMessage";
import { useIsMobile } from "@/hooks/use-mobile";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

type Approval = {
  approval_id: string;
  tool_name: string;
  input: Record<string, unknown>;
  status: "pending" | "approved" | "declined";
};

type Msg = {
  role: "user" | "assistant";
  content: string;
  approval?: Approval;
};

const QUICK_PROMPTS = [
  "Show me bestsellers",
  "Cosy gift under ₹1000",
  "Track my order",
  "Cancel my latest order",
  "International shipping?",
  "Tell me about artisans",
];

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/agent-run`;
const APPROVE_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/agent-approve`;

const ChatAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [threadId, setThreadId] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) inputRef.current.focus();
  }, [isOpen]);

  const sendMessage = useCallback(
    async (text: string) => {
      if (!text.trim() || isLoading) return;
      const userMsg: Msg = { role: "user", content: text.trim() };
      setMessages((prev) => [...prev, userMsg]);
      setInput("");
      setIsLoading(true);

      abortRef.current = new AbortController();
      let assistantSoFar = "";

      const upsertAssistant = (chunk: string) => {
        assistantSoFar += chunk;
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.role === "assistant" && !last.approval) {
            return prev.map((m, i) => (i === prev.length - 1 ? { ...m, content: assistantSoFar } : m));
          }
          return [...prev, { role: "assistant", content: assistantSoFar }];
        });
      };

      const appendApproval = (approval: Approval) => {
        setMessages((prev) => [...prev, { role: "assistant", content: "", approval }]);
      };

      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const authToken = sessionData.session?.access_token ?? import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

        const resp = await fetch(CHAT_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({
            messages: [...messages.filter((m) => !m.approval).map((m) => ({ role: m.role, content: m.content })), userMsg],
            thread_id: threadId,
          }),
          signal: abortRef.current.signal,
        });

        if (!resp.ok) {
          const err = await resp.json().catch(() => ({ error: "Assistant unavailable" }));
          toast({ title: "Assistant", description: err.error, variant: "destructive" });
          setIsLoading(false);
          return;
        }
        if (!resp.body) {
          toast({ title: "No response", variant: "destructive" });
          setIsLoading(false);
          return;
        }

        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buf = "";
        let done = false;
        let currentEvent: string | null = null;

        while (!done) {
          const { done: d, value } = await reader.read();
          if (d) break;
          buf += decoder.decode(value, { stream: true });
          let nl: number;
          while ((nl = buf.indexOf("\n")) !== -1) {
            let line = buf.slice(0, nl);
            buf = buf.slice(nl + 1);
            if (line.endsWith("\r")) line = line.slice(0, -1);
            if (line === "") { currentEvent = null; continue; }
            if (line.startsWith("event: ")) { currentEvent = line.slice(7).trim(); continue; }
            if (!line.startsWith("data: ")) continue;
            const payload = line.slice(6).trim();
            if (payload === "[DONE]") { done = true; break; }

            if (currentEvent === "thread") {
              try { const p = JSON.parse(payload); if (p.thread_id) setThreadId(p.thread_id); } catch {}
              currentEvent = null; continue;
            }
            if (currentEvent === "approval") {
              try { appendApproval(JSON.parse(payload)); } catch {}
              currentEvent = null; continue;
            }
            try {
              const parsed = JSON.parse(payload);
              if (parsed.error) toast({ title: "Assistant", description: parsed.error, variant: "destructive" });
              const c = parsed.choices?.[0]?.delta?.content;
              if (c) upsertAssistant(c);
            } catch { /* ignore */ }
          }
        }
      } catch (e: any) {
        if (e?.name !== "AbortError") {
          toast({ title: "Connection error", description: "Could not reach Mitti.", variant: "destructive" });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [messages, isLoading, threadId]
  );

  const handleApproval = async (approval: Approval, decision: "approve" | "decline") => {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const authToken = sessionData.session?.access_token;
      if (!authToken) {
        toast({ title: "Please sign in", variant: "destructive" });
        return;
      }
      setMessages((prev) =>
        prev.map((m) =>
          m.approval?.approval_id === approval.approval_id
            ? { ...m, approval: { ...m.approval!, status: decision === "approve" ? "approved" : "declined" } }
            : m
        )
      );
      const resp = await fetch(APPROVE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ approval_id: approval.approval_id, decision }),
      });
      const data = await resp.json();
      const text = data.message ?? (resp.ok ? "Done." : data.error ?? "Failed.");
      setMessages((prev) => [...prev, { role: "assistant", content: (resp.ok ? "✅ " : "⚠️ ") + text }]);
    } catch {
      toast({ title: "Could not submit decision", variant: "destructive" });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  const resetChat = () => {
    if (abortRef.current) abortRef.current.abort();
    setMessages([]);
    setThreadId(null);
    setIsLoading(false);
  };

  const panelClass = isMobile
    ? "fixed inset-0 z-[60]"
    : "fixed bottom-24 right-5 z-[60] w-[400px] h-[560px] rounded-2xl shadow-elevated";

  return (
    <>
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-24 right-5 z-50 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-elevated flex items-center justify-center hover:scale-105 transition-transform"
          aria-label="Open AI shopping assistant"
        >
          <Sparkles className="w-6 h-6" />
        </button>
      )}

      {isOpen && (
        <div className={`${panelClass} flex flex-col bg-background border border-border overflow-hidden`}>
          <div className="flex items-center justify-between px-4 py-3 border-b bg-primary text-primary-foreground rounded-t-2xl">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5" />
              <div>
                <p className="font-semibold text-sm">Mitti 🪔</p>
                <p className="text-xs opacity-80">Your companion for artisan stories</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={resetChat} className="p-1.5 rounded-full hover:bg-primary-foreground/20" aria-label="Reset">
                <RotateCcw className="w-4 h-4" />
              </button>
              <button onClick={() => setIsOpen(false)} className="p-1.5 rounded-full hover:bg-primary-foreground/20" aria-label="Close">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {messages.length === 0 && (
              <div className="text-center py-6 space-y-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                  <MessageCircle className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-sm text-foreground">Namaste, I'm Mitti! 🙏</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Ask me about crafts, gifts, your orders — I can also help cancel or update them with your approval.
                  </p>
                </div>
                <div className="flex flex-wrap justify-center gap-2">
                  {QUICK_PROMPTS.map((prompt) => (
                    <button
                      key={prompt}
                      onClick={() => sendMessage(prompt)}
                      className="text-xs px-3 py-1.5 rounded-full border border-border bg-background hover:bg-accent text-foreground"
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) =>
              msg.approval ? (
                <ApprovalCard
                  key={`${msg.approval.approval_id}-${i}`}
                  approval={msg.approval}
                  onDecide={(d) => handleApproval(msg.approval!, d)}
                />
              ) : (
                <ChatMessage key={i} role={msg.role} content={msg.content} />
              )
            )}

            {isLoading && messages[messages.length - 1]?.role !== "assistant" && (
              <div className="flex items-center gap-2 text-muted-foreground text-xs">
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Thinking...
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="p-3 border-t flex gap-2">
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Mitti about crafts..."
              disabled={isLoading}
              className="flex-1 h-9 text-sm"
            />
            <Button type="submit" size="icon" disabled={!input.trim() || isLoading} className="h-9 w-9 flex-shrink-0">
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      )}
    </>
  );
};

function ApprovalCard({ approval, onDecide }: { approval: Approval; onDecide: (d: "approve" | "decline") => void }) {
  const disabled = approval.status !== "pending";
  const label = approval.tool_name.replace(/_/g, " ");
  return (
    <div className="rounded-xl border border-amber-300/60 bg-amber-50/60 dark:bg-amber-950/30 p-3 text-xs space-y-2">
      <div className="font-medium text-amber-900 dark:text-amber-100">Approval needed: {label}</div>
      <pre className="text-[11px] bg-background/60 rounded p-2 overflow-x-auto">
        {JSON.stringify(approval.input, null, 2)}
      </pre>
      {approval.status === "pending" ? (
        <div className="flex gap-2">
          <Button size="sm" className="h-7 px-3 text-xs" onClick={() => onDecide("approve")} disabled={disabled}>
            <Check className="w-3 h-3 mr-1" /> Approve
          </Button>
          <Button size="sm" variant="outline" className="h-7 px-3 text-xs" onClick={() => onDecide("decline")} disabled={disabled}>
            <Ban className="w-3 h-3 mr-1" /> Decline
          </Button>
        </div>
      ) : (
        <div className="text-[11px] text-muted-foreground italic">
          {approval.status === "approved" ? "Approved — running…" : "Declined."}
        </div>
      )}
    </div>
  );
}

export default ChatAssistant;
